import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../integrations/supabase/client'
import { ArrowLeft, Save, LogOut } from 'lucide-react'
import { Header } from './Header'
import SideMenu from './SideMenu'

export default function ProfilePage() {
  const [sideMenuOpen, setSideMenuOpen] = useState(false)
  const [theme, setTheme] = useState<'dark' | 'light'>(() =>
    (typeof window !== 'undefined' && document.documentElement.getAttribute('data-theme') as 'light' | 'dark') || 'light'
  )
  useEffect(() => { document.documentElement.setAttribute('data-theme', theme) }, [theme])

  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [nome, setNome] = useState('')
  const [cognome, setCognome] = useState('')
  const [email, setEmail] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null)

  useEffect(() => {
    ;(async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { navigate('/auth'); return }
      setEmail(user.email ?? '')

      const { data: profile } = await supabase
        .from('profiles')
        .select('nome, cognome')
        .eq('id', user.id)
        .single()

      if (profile) {
        setNome(profile.nome ?? '')
        setCognome(profile.cognome ?? '')
      }
      setLoading(false)
    })()
  }, [navigate])

  const handleSaveProfile = async () => {
    setSaving(true)
    setMessage(null)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Non autenticato')

      const { error } = await supabase
        .from('profiles')
        .update({ nome, cognome, updated_at: new Date().toISOString() })
        .eq('id', user.id)

      if (error) throw error
      setMessage({ type: 'ok', text: 'Profilo aggiornato con successo!' })
    } catch (err: any) {
      setMessage({ type: 'err', text: err.message })
    } finally {
      setSaving(false)
    }
  }

  const handleChangePassword = async () => {
    if (newPassword.length < 6) {
      setMessage({ type: 'err', text: 'La password deve avere almeno 6 caratteri.' })
      return
    }
    setSaving(true)
    setMessage(null)
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword })
      if (error) throw error
      setNewPassword('')
      setMessage({ type: 'ok', text: 'Password aggiornata con successo!' })
    } catch (err: any) {
      setMessage({ type: 'err', text: err.message })
    } finally {
      setSaving(false)
    }
  }

  const handleLogout = async () => {
    await supabase.auth.signOut()
    navigate('/')
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-0)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-2)' }}>
        Caricamento...
      </div>
    )
  }

  return (
    <div className="app">
      <Header onMenuOpen={() => setSideMenuOpen(true)} />
      <SideMenu open={sideMenuOpen} onClose={() => setSideMenuOpen(false)} theme={theme} onToggleTheme={() => setTheme(p => p === 'dark' ? 'light' : 'dark')} />

      <div style={{ padding: '80px 24px 48px', maxWidth: 520, margin: '0 auto', fontFamily: 'var(--font-body)' }}>
        <button
          onClick={() => navigate('/')}
          style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 24, padding: 0 }}
        >
          <ArrowLeft size={14} /> Torna alla home
        </button>

        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 800, color: 'var(--text-0)', marginBottom: 32 }}>
          Il mio profilo
        </h1>

        {message && (
          <div style={{
            background: message.type === 'ok' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
            border: `1px solid ${message.type === 'ok' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
            borderRadius: 8, padding: '10px 14px', fontSize: 12,
            color: message.type === 'ok' ? '#10b981' : '#ef4444',
            marginBottom: 20,
          }}>
            {message.text}
          </div>
        )}

        {/* Profile info */}
        <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border-md)', borderRadius: 12, padding: 24, marginBottom: 20 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-0)', marginBottom: 16 }}>Informazioni personali</h2>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={labelStyle}>Nome</label>
              <input value={nome} onChange={e => setNome(e.target.value)} placeholder="Nome" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Cognome</label>
              <input value={cognome} onChange={e => setCognome(e.target.value)} placeholder="Cognome" style={inputStyle} />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Email</label>
            <input value={email} disabled style={{ ...inputStyle, opacity: 0.6, cursor: 'not-allowed' }} />
          </div>

          <button onClick={handleSaveProfile} disabled={saving} style={btnStyle}>
            <Save size={14} />
            {saving ? 'Salvataggio...' : 'Salva profilo'}
          </button>
        </div>

        {/* Change password */}
        <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border-md)', borderRadius: 12, padding: 24, marginBottom: 20 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-0)', marginBottom: 16 }}>Cambia password</h2>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Nuova password</label>
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" minLength={6} style={inputStyle} />
          </div>

          <button onClick={handleChangePassword} disabled={saving || !newPassword} style={{ ...btnStyle, opacity: !newPassword ? 0.5 : 1 }}>
            Aggiorna password
          </button>
        </div>

        {/* Logout */}
        <button onClick={handleLogout} style={{
          background: 'rgba(239,68,68,0.1)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 10,
          padding: '12px 24px',
          fontSize: 13,
          fontWeight: 600,
          color: '#ef4444',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          width: '100%',
          justifyContent: 'center',
        }}>
          <LogOut size={14} />
          Esci dall'account
        </button>
      </div>
    </div>
  )
}

const labelStyle: React.CSSProperties = {
  fontSize: 11, color: 'var(--text-2)', textTransform: 'uppercase',
  letterSpacing: '0.08em', marginBottom: 4, display: 'block',
}

const inputStyle: React.CSSProperties = {
  width: '100%', background: 'var(--bg-1)', border: '1px solid var(--border-md)',
  borderRadius: 8, padding: '10px 12px', fontSize: 13, color: 'var(--text-0)',
  outline: 'none', fontFamily: 'var(--font-body)',
}

const btnStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #06b6d4, #0891b2)',
  color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px',
  fontSize: 13, fontWeight: 700, cursor: 'pointer',
  display: 'flex', alignItems: 'center', gap: 8,
}
